/**
 * Peeling the typography wrapper off an email cell, and putting it back byte for byte.
 *
 * WHAT THESE CELLS ACTUALLY LOOK LIKE. Measured, not guessed: every rich cell in
 * codigo-fan-sale-es (and in the PRO templates before it) has the same shape — one
 * wrapper element carrying the cell's typography, containing an ordinary rich text
 * document:
 *
 *   <div style="color:#101b24;font-family:Arial,…;font-size:16px;line-height:150%;…">
 *     <p style="margin:0;margin-bottom:0">&nbsp;</p>
 *     <p style="margin:0">La fan sale empieza hoy… <strong>Charli xcx</strong> y más!</p>
 *   </div>
 *
 * That wrapper's style IS the cell's typography — the colour, the family, the size, the
 * line height that the whole email depends on. It is not the copywriter's to edit here,
 * and it is not something a rich text editor should have to model. So it is peeled off
 * before the editor sees anything, kept as an opaque string, and concatenated back on
 * every save.
 *
 * THE ONE PROPERTY THAT MATTERS: rewrapChrome(unwrapChrome(x)) === x, for every x, byte
 * for byte. prefix/inner/suffix are three slices of the original string at two offsets,
 * so the identity is true by construction rather than by careful reassembly — see the
 * test for it holding on every real cell of codigo-fan-sale-es verbatim.
 *
 * CommonJS with a plain-node test because this admin has no Jest, no Vitest and no RTL.
 */

// Elements that never have a closing tag. `<br>` is the one that actually turns up in
// these emails; the rest are here so a stray one cannot throw the depth count off.
const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

// Elements that make a body "a document" rather than "a run of words". Peeling stops
// when what is left is inline, because a rich text editor's document is a list of
// blocks: hand it loose inline content and it invents a <p> that was never there.
const BLOCK_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'center', 'div', 'dl', 'figure',
  'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav',
  'ol', 'p', 'pre', 'section', 'table', 'tbody', 'td', 'tr', 'ul',
]);

// Comments first, so a `<` inside a comment cannot be read as a tag. Attribute values
// are matched as quoted runs so that a '>' inside style="…" does not end the tag, and
// unquoted/bare attributes (`<div class style="…">` really is in the footer cell) still
// match. Newlines inside a tag are ordinary characters — `<h1 \nstyle="…">` is real.
const TAG_PATTERN = /<!--[\s\S]*?-->|<\/?([a-zA-Z][a-zA-Z0-9:-]*)(?:"[^"]*"|'[^']*'|[^>"'])*>/g;

/**
 * The top level nodes of a fragment, as offsets into it.
 *
 * Returns null when the fragment is not balanced — an unclosed <p>, a stray </div>,
 * anything this scanner cannot account for. Null means "I do not understand this", and
 * every caller treats that as a reason to do nothing rather than a reason to guess.
 *
 * [{ type: 'element'|'text'|'comment', name, start, end, contentStart, contentEnd }]
 */
function topLevelNodes(html) {
  if (typeof html !== 'string') return null;

  const nodes = [];
  let depth = 0;
  let cursor = 0;
  let openName = '';
  let openStart = 0;
  let openContentStart = 0;

  const pushTextBefore = (index) => {
    if (index > cursor) nodes.push({ type: 'text', start: cursor, end: index });
  };

  TAG_PATTERN.lastIndex = 0;
  let match = TAG_PATTERN.exec(html);

  while (match !== null) {
    const raw = match[0];
    const isComment = raw.slice(0, 4) === '<!--';

    if (isComment) {
      if (depth === 0) {
        pushTextBefore(match.index);
        nodes.push({ type: 'comment', start: match.index, end: match.index + raw.length });
        cursor = match.index + raw.length;
      }
    } else {
      const name = match[1].toLowerCase();
      const isClosing = raw[1] === '/';
      const isEmpty = VOID_TAGS.has(name) || /\/>$/.test(raw);

      if (isClosing) {
        depth -= 1;
        if (depth < 0) return null;
        if (depth === 0) {
          nodes.push({
            type: 'element',
            name: openName,
            start: openStart,
            end: match.index + raw.length,
            contentStart: openContentStart,
            contentEnd: match.index,
          });
          cursor = match.index + raw.length;
        }
      } else if (isEmpty) {
        if (depth === 0) {
          pushTextBefore(match.index);
          nodes.push({
            type: 'element',
            name,
            start: match.index,
            end: match.index + raw.length,
            contentStart: null,
            contentEnd: null,
            empty: true,
          });
          cursor = match.index + raw.length;
        }
      } else {
        if (depth === 0) {
          pushTextBefore(match.index);
          openName = name;
          openStart = match.index;
          openContentStart = match.index + raw.length;
        }
        depth += 1;
      }
    }

    match = TAG_PATTERN.exec(html);
  }

  if (depth !== 0) return null;
  pushTextBefore(html.length);
  return nodes;
}

// Whitespace between two block elements is formatting, not content. It is still kept
// verbatim — it ends up inside prefix or suffix — it just does not count as a second
// top level node when deciding whether one element wraps everything.
const isBlankText = (html, node) => node.type === 'text'
  && html.slice(node.start, node.end).trim() === '';

const isBlockElement = node => node.type === 'element' && BLOCK_TAGS.has(node.name);

// A list is copy, not chrome. Its <li>s are blocks by the test below, so without this a
// cell that is nothing but a <ul> was peeled down to its items — and then the editor was
// handed bare <li>s, wrote a <ul> of its own around them, and rewrapChrome put the
// original <ul> back over that: `<ul><ul><li>…` in a sent email. The <ul> is the document
// here in the same way the <h1> is in the heading cell, so peeling stops at it.
const LIST_TAGS = new Set(['ul', 'ol']);

/**
 * The one element that wraps everything in this fragment AND has a document inside it,
 * or null when there is no such element — which is most of the time.
 */
function peelableWrapper(html) {
  const nodes = topLevelNodes(html);
  if (nodes === null) return null;

  const meaningful = nodes.filter(node => !isBlankText(html, node));
  if (meaningful.length !== 1) return null;

  const only = meaningful[0];
  if (only.type !== 'element' || only.empty) return null;
  if (LIST_TAGS.has(only.name)) return null;

  const bodyNodes = topLevelNodes(html.slice(only.contentStart, only.contentEnd));
  if (bodyNodes === null || !bodyNodes.some(isBlockElement)) return null;

  return only;
}

/**
 * Split a cell into { prefix, inner, suffix }.
 *
 * `prefix` is the opening tag(s) of the wrapper(s), `inner` the document inside them,
 * `suffix` the closing tag(s). When there is no wrapper — or when peeling it would
 * leave inline content behind — prefix and suffix are empty and inner is the input,
 * unchanged.
 *
 * PEELING STOPS at a wrapper whose body is inline. `<p style="…"><span>{{code}}</span></p>`
 * keeps its <p>: it is the block the editor needs, and stripping it would hand the editor
 * a bare run of words that it would re-wrap in a <p> of its own invention. The same rule
 * is why `<h1 …><strong><span>Gracias…</span></strong></h1>` is not peeled at all — the
 * <h1> is the document, not chrome around one.
 */
function unwrapChrome(html) {
  let prefix = '';
  let inner = typeof html === 'string' ? html : '';
  let suffix = '';

  for (;;) {
    const wrapper = peelableWrapper(inner);
    if (wrapper === null) break;

    prefix += inner.slice(0, wrapper.contentStart);
    suffix = inner.slice(wrapper.contentEnd) + suffix;
    inner = inner.slice(wrapper.contentStart, wrapper.contentEnd);
  }

  return { prefix, inner, suffix };
}

/** The inverse. Concatenation, deliberately: anything cleverer could not be exact. */
function rewrapChrome(parts) {
  if (!parts) return '';
  return `${parts.prefix || ''}${parts.inner || ''}${parts.suffix || ''}`;
}

module.exports = {
  BLOCK_TAGS, VOID_TAGS, topLevelNodes, unwrapChrome, rewrapChrome,
};
